import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";

import { EquipmentSilhouette } from "./EquipmentSilhouette";
import { formatDollarsRound } from "./pricing";
import { effectiveCardSummary } from "./typeSummary";
import type { TypeSummary } from "./types";

// Card for a single piece of equipment, used on the category landing
// page and the search results page. Click-anywhere navigates to the
// equipment detail; the entire card is the link (with a focusable inner heading
// for AT users). Photo is 4:3 per brand guidance, neutral background
// fallback when `featured_image_url` is null (FR-10 edge case).
export interface EquipmentCardProps {
  type: TypeSummary;
  categorySlug: string;
  className?: string;
}

export function EquipmentCard({ type, categorySlug, className }: EquipmentCardProps): JSX.Element {
  return (
    <article
      className={cn(
        "group relative flex h-full flex-col overflow-hidden rounded-lg border border-zinc-200 bg-brand-white transition",
        "hover:-translate-y-0.5 hover:border-brand-red/40 hover:shadow-lg",
        "focus-within:ring-2 focus-within:ring-brand-red focus-within:ring-offset-2",
        className
      )}
    >
      <div className="relative aspect-[4/3] w-full overflow-hidden bg-zinc-100">
        {type.featured_image_url ? (
          <img
            src={type.featured_image_url}
            alt=""
            loading="lazy"
            decoding="async"
            className="h-full w-full object-cover transition-transform duration-300 motion-safe:group-hover:scale-[1.04]"
          />
        ) : (
          <EquipmentSilhouette categorySlug={categorySlug} />
        )}
        {/* Hover-only brand-red accent strip along the bottom of the image. */}
        <span
          aria-hidden="true"
          className="absolute inset-x-0 bottom-0 h-0.5 bg-brand-red opacity-0 transition group-hover:opacity-100"
        />
      </div>
      <div className="flex flex-1 flex-col gap-1.5 p-4">
        <h3 className="line-clamp-2 text-base font-semibold text-brand-black">
          <Link
            to={`/c/${categorySlug}/${type.slug}`}
            className="focus-visible:outline-none"
            // The whole card is clickable via the absolutely-positioned
            // overlay below; this <Link> exists for keyboard/AT users.
          >
            <span aria-hidden="true" className="absolute inset-0" />
            {type.name}
          </Link>
        </h3>
        <p className="line-clamp-2 text-sm text-zinc-600">{effectiveCardSummary(type)}</p>
        <p className="mt-auto pt-2 text-sm text-zinc-700">
          {type.from_price_per_week_cents != null
            ? <>from <span className="font-semibold text-brand-black">{formatDollarsRound(type.from_price_per_week_cents)}</span> <span className="text-zinc-500">/ week</span></>
            : <span className="text-zinc-500">Contact for pricing</span>}
        </p>
      </div>
    </article>
  );
}

